import React from 'react';
import type { ScopeTreeEntry } from './ScopeTreeView';

interface ScopeEntryDetailProps {
  entries: ScopeTreeEntry[];
  selectedKey: string | null;
  readOnly: boolean;
  onRename: (key: string, title: string) => void;
  onAddChild: (parentKey: string) => void;
  onRetire: (key: string) => void;
  onRestore: (key: string) => void;
}

/**
 * Right-pane detail for the selected entry. Inline rename, plus
 * Add child / Retire / Restore actions. The actual edits are owned
 * by ScopesAdmin via a working copy of the entries — this component
 * just calls back.
 *
 * When readOnly is true (no Scope selected or no write rights), all
 * controls render as plain text and the buttons are hidden.
 */
export const ScopeEntryDetail: React.FC<ScopeEntryDetailProps> = ({
  entries,
  selectedKey,
  readOnly,
  onRename,
  onAddChild,
  onRetire,
  onRestore
}) => {
  if (!selectedKey) {
    return (
      <div className="scope-detail-empty">
        No entry is selected. Use the stakeholder editor below for Scope-wide defaults, or select an entry on the left to edit entry-specific details.
      </div>
    );
  }
  const entry = entries.find((candidate) => candidate.scopeEntryKey === selectedKey);
  if (!entry) {
    return <div className="scope-detail-empty">That entry is no longer part of this Scope.</div>;
  }
  const parent = entry.parentScopeEntryKey
    ? entries.find((candidate) => candidate.scopeEntryKey === entry.parentScopeEntryKey)
    : null;

  return (
    <>
      <div className="scope-detail-fields">
        <span className="label">Title</span>
        {readOnly ? (
          <span className="value">{entry.title || '(untitled)'}</span>
        ) : (
          <input
            className="input"
            type="text"
            value={entry.title}
            onChange={(event) => onRename(entry.scopeEntryKey, event.target.value)}
            maxLength={256}
          />
        )}

        <span className="label">Key</span>
        <span className="value"><code>{entry.scopeEntryKey}</code></span>

        <span className="label">Parent</span>
        <span className={`value${parent ? '' : ' muted'}`}>
          {parent ? parent.title : 'Top level'}
        </span>

        <span className="label">State</span>
        <span className="value">{entry.isActive ? 'Active' : 'Retired'}</span>
      </div>

      {!readOnly && (
        <div className="scope-detail-actions">
          <button
            className="btn secondary"
            type="button"
            onClick={() => onAddChild(entry.scopeEntryKey)}
            disabled={!entry.isActive}
            title={entry.isActive ? 'Add a child entry under this one' : 'Restore this entry first'}
          >
            Add child entry
          </button>
          {entry.isActive ? (
            <button
              className="btn secondary"
              type="button"
              onClick={() => onRetire(entry.scopeEntryKey)}
            >
              Retire
            </button>
          ) : (
            <button
              className="btn secondary"
              type="button"
              onClick={() => onRestore(entry.scopeEntryKey)}
            >
              Restore
            </button>
          )}
        </div>
      )}

      {readOnly && (
        <div className="scope-detail-note">
          This Scope is read-only here. Use the Import Draft section to publish a new revision.
        </div>
      )}
    </>
  );
};

export default ScopeEntryDetail;
